// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";

const AuthContext = createContext();

/**
 * Check if JWT access token is expired
 */
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---------------------------------
  // LOAD USER ON REFRESH + AUTO LOGOUT
  // ---------------------------------
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userInfo");

      if (raw) {
        const parsed = JSON.parse(raw);

        // 🔥 AUTO LOGOUT IF TOKEN IS INVALID / EXPIRED
        if (!parsed?.accessToken || isTokenExpired(parsed.accessToken)) {
          localStorage.removeItem("userInfo");
          setAuthUser(null);
          window.location.href = "/";
          return;
        }

        setAuthUser(parsed);

        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${parsed.accessToken}`;
      }
    } catch (err) {
      console.error("Auth restore failed:", err);
      localStorage.removeItem("userInfo");
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------------------
  // LOGIN
  // ---------------------------
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });

      if (!data?.accessToken || !data?.user) {
        return { ok: false, message: "Invalid response from server" };
      }

      localStorage.setItem("userInfo", JSON.stringify(data));
      setAuthUser(data);

      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${data.accessToken}`;

      if (data.user.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/dashboard");
      }

      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        message: err.response?.data?.message || "Login failed",
      };
    }
  };

  // ---------------------------
  // REGISTER
  // ---------------------------
  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return { ok: true, data };
    } catch (err) {
      return {
        ok: false,
        message: err.response?.data?.message || "Registration failed",
      };
    }
  };

  // ---------------------------
  // LOGOUT (FORCE LANDING PAGE)
  // ---------------------------
  const logout = () => {
    localStorage.removeItem("userInfo");
    setAuthUser(null);
    delete api.defaults.headers.common["Authorization"];

    // 🔥 Hard redirect to bypass route guards
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        authUser,
        loading,
        setAuthUser,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
